import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    Paper,
    Checkbox,
    LinearProgress,
    IconButton,
    Chip,
    Divider,
    CircularProgress,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Close as CloseIcon,
    Description as PdfIcon,
    CheckCircle as CheckIcon,
    Refresh as RetryIcon,
    Flight as FlightIcon,
    Hotel as HotelIcon,
    DirectionsCar as CarIcon,
    Attractions as ActivityIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { api } from '../../utils/api';

// ============================================================================
// TYPES — mirror TripImportCard's extracted-data shapes
// ============================================================================

interface ExtractedFlight {
    airline: string;
    flightNumber: string;
    departure: { airport: string; city?: string; date: string; time: string };
    arrival: { airport: string; city?: string; date: string; time: string };
    confirmationNumber?: string;
    passengerNames?: string[];
    price?: number;
}

interface ExtractedHotel {
    name: string;
    address?: string;
    city?: string;
    checkIn: string;
    checkOut: string;
    confirmationNumber?: string;
    guestNames?: string[];
    price?: number;
    roomType?: string;
}

interface ExtractedCar {
    company: string;
    pickupLocation: string;
    dropoffLocation: string;
    pickupDate: string;
    dropoffDate: string;
    confirmationNumber?: string;
    driverName?: string;
    price?: number;
    vehicleType?: string;
}

interface ExtractedActivity {
    name: string;
    date: string;
    time?: string;
    location?: string;
    confirmationNumber?: string;
    price?: number;
}

interface ExtractedTripData {
    title?: string;
    destination?: string;
    startDate?: string;
    endDate?: string;
    bookingSource?: string;
    flights: ExtractedFlight[];
    hotels: ExtractedHotel[];
    cars: ExtractedCar[];
    activities: ExtractedActivity[];
    totalPrice?: number;
    currency?: string;
}

// Generic flat representation. Used for the review checklist.
interface NormalizedItem {
    key: string;                    // unique within this review session
    type: 'flight' | 'hotel' | 'car' | 'activity';
    title: string;
    subtitle?: string;
    payload: Record<string, unknown>;  // exactly what we POST to /trips/:id/items
}

type ImportStep = 'upload' | 'processing' | 'review' | 'saving' | 'success' | 'error';

interface TripItemImportCardProps {
    tripId: string;
    onItemsAdded?: () => void;
}

// ============================================================================
// MAPPERS — convert extracted shapes into the items API payload
// The /trips/:id/items endpoint accepts the flat shape used by AddTripItemDialog.
// ============================================================================

const mapFlight = (f: ExtractedFlight, idx: number): NormalizedItem => {
    const title = `${f.airline} ${f.flightNumber}`.trim();
    const route = `${f.departure.airport}${f.arrival.airport ? ' \u2192 ' + f.arrival.airport : ''}`;
    return {
        key: `flight-${idx}`,
        type: 'flight',
        title,
        subtitle: `${route}${f.departure.date ? ' on ' + f.departure.date : ''}`,
        payload: {
            item_type: 'flight',
            title,
            location: route || undefined,
            start_date: f.departure.date || undefined,
            end_date: f.arrival.date || undefined,
            start_time: f.departure.time || undefined,
            end_time: f.arrival.time || undefined,
            estimated_cost: typeof f.price === 'number' ? f.price : undefined,
            confirmation_number: f.confirmationNumber || undefined,
        },
    };
};

const mapHotel = (h: ExtractedHotel, idx: number): NormalizedItem => {
    const location = h.address || h.city || undefined;
    return {
        key: `hotel-${idx}`,
        type: 'hotel',
        title: h.name,
        subtitle: `${location ? location + ' \u00b7 ' : ''}${h.checkIn} \u2192 ${h.checkOut}`,
        payload: {
            item_type: 'hotel',
            title: h.name,
            location,
            start_date: h.checkIn || undefined,
            end_date: h.checkOut || undefined,
            estimated_cost: typeof h.price === 'number' ? h.price : undefined,
            confirmation_number: h.confirmationNumber || undefined,
            notes: h.roomType ? `Room: ${h.roomType}` : undefined,
        },
    };
};

const mapCar = (c: ExtractedCar, idx: number): NormalizedItem => {
    const title = `${c.company}${c.vehicleType ? ' ' + c.vehicleType : ''}`.trim();
    const route = `${c.pickupLocation}${c.dropoffLocation ? ' \u2192 ' + c.dropoffLocation : ''}`;
    return {
        key: `car-${idx}`,
        type: 'car',
        title,
        subtitle: `${route} \u00b7 ${c.pickupDate} \u2192 ${c.dropoffDate}`,
        payload: {
            item_type: 'car',
            title,
            location: route || undefined,
            start_date: c.pickupDate || undefined,
            end_date: c.dropoffDate || undefined,
            estimated_cost: typeof c.price === 'number' ? c.price : undefined,
            confirmation_number: c.confirmationNumber || undefined,
        },
    };
};

const mapActivity = (a: ExtractedActivity, idx: number): NormalizedItem => {
    return {
        key: `activity-${idx}`,
        type: 'activity',
        title: a.name,
        subtitle: `${a.location ? a.location + ' \u00b7 ' : ''}${a.date}${a.time ? ' at ' + a.time : ''}`,
        payload: {
            item_type: 'activity',
            title: a.name,
            location: a.location || undefined,
            start_date: a.date || undefined,
            start_time: a.time || undefined,
            estimated_cost: typeof a.price === 'number' ? a.price : undefined,
            confirmation_number: a.confirmationNumber || undefined,
        },
    };
};

const normalizeAll = (data: ExtractedTripData): NormalizedItem[] => {
    return [
        ...data.flights.map(mapFlight),
        ...data.hotels.map(mapHotel),
        ...data.cars.map(mapCar),
        ...data.activities.map(mapActivity),
    ];
};

const iconForType = (type: NormalizedItem['type']) => {
    switch (type) {
        case 'flight': return <FlightIcon sx={{ color: '#2196F3' }} />;
        case 'hotel': return <HotelIcon sx={{ color: '#4A6FA5' }} />;
        case 'car': return <CarIcon sx={{ color: '#607D8B' }} />;
        case 'activity': return <ActivityIcon sx={{ color: '#2D6E6A' }} />;
    }
};

// ============================================================================
// COMPONENT
// ============================================================================

const TripItemImportCard: React.FC<TripItemImportCardProps> = ({ tripId, onItemsAdded }) => {
    const [step, setStep] = useState<ImportStep>('upload');
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [normalizedItems, setNormalizedItems] = useState<NormalizedItem[]>([]);
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [savedCount, setSavedCount] = useState(0);
    const [failedCount, setFailedCount] = useState(0);
    const [isOcrPolling, setIsOcrPolling] = useState(false);

    // ----- Dropzone -----
    const onDrop = useCallback((acceptedFiles: File[]) => {
        const pdf = acceptedFiles.find((f) => f.type === 'application/pdf');
        if (pdf) {
            setFile(pdf);
            setError(null);
        } else {
            setError('Please upload a PDF file.');
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/pdf': ['.pdf'] },
        maxFiles: 1,
        multiple: false,
    });

    // ----- Process: upload to S3, then call import/process -----
    const handleUploadAndProcess = async () => {
        if (!file) return;
        setStep('processing');
        setIsOcrPolling(false);
        setError(null);

        try {
            // 1. Get presigned URL
            const uploadUrlResponse = await api.post('/trips/import/upload-url', {
                filename: file.name,
                contentType: 'application/pdf',
            });
            if (!uploadUrlResponse.ok) {
                throw new Error('Failed to get upload URL');
            }
            const { presignedUrl, s3Key } = await uploadUrlResponse.json();

            // 2. PUT file to S3
            const uploadResponse = await fetch(presignedUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': 'application/pdf' },
            });
            if (!uploadResponse.ok) {
                throw new Error('Failed to upload PDF');
            }

            // 3. Start processing
            const processResponse = await api.post('/trips/import/process', { s3Key });
            if (!processResponse.ok) {
                const err = await processResponse.json();
                throw new Error(err.error || err.message || 'Failed to process PDF');
            }
            const startData = await processResponse.json();

            // 4. Fast path OR poll loop for OCR
            let extractedData: any;
            if (startData.status === 'ocr_pending' && startData.jobId) {
                setIsOcrPolling(true);
                const jobId: string = startData.jobId;
                const maxWaitMs = 240000; // 4 minutes — real multi-page PDFs take 1-3 min
                const pollIntervalMs = 3000;
                const pollStart = Date.now();
                while (Date.now() - pollStart < maxWaitMs) {
                    await new Promise(r => setTimeout(r, pollIntervalMs));
                    const pollResponse = await api.post('/trips/import/process', { jobId });
                    if (!pollResponse.ok) {
                        const err = await pollResponse.json();
                        throw new Error(err.error || 'OCR polling failed');
                    }
                    const pollData = await pollResponse.json();
                    if (pollData.status === 'processing') continue;
                    if (pollData.status === 'done' && pollData.extractedData) {
                        extractedData = pollData.extractedData;
                        break;
                    }
                    throw new Error(pollData.error || 'OCR failed');
                }
                if (!extractedData) {
                    throw new Error('OCR timed out. Try a different PDF or add items manually.');
                }
            } else if (startData.extractedData) {
                extractedData = startData.extractedData;
            } else {
                throw new Error('Unexpected response from server');
            }
            setIsOcrPolling(false);

            // 5. Normalize for review
            const items = normalizeAll(extractedData as ExtractedTripData);
            if (items.length === 0) {
                setError('No items found in this PDF. Try a different file or add items manually.');
                setStep('error');
                return;
            }
            setNormalizedItems(items);
            // Default: all items checked
            setSelected(Object.fromEntries(items.map((i) => [i.key, true])));
            setStep('review');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unexpected error occurred');
            setStep('error');
        }
    };

    // ----- Save selected items to the trip -----
    const handleSaveSelected = async () => {
        const itemsToSave = normalizedItems.filter((item) => selected[item.key]);
        if (itemsToSave.length === 0) {
            setError('Select at least one item to add.');
            return;
        }

        setStep('saving');
        setError(null);
        setSavedCount(0);
        setFailedCount(0);

        let saved = 0;
        let failed = 0;

        for (const item of itemsToSave) {
            try {
                const response = await api.post(`/trips/${tripId}/items`, item.payload);
                const data = await response.json();
                if (response.ok && data.success) {
                    saved += 1;
                    setSavedCount(saved);
                } else {
                    failed += 1;
                    setFailedCount(failed);
                }
            } catch {
                failed += 1;
                setFailedCount(failed);
            }
        }

        if (saved > 0 && onItemsAdded) {
            onItemsAdded();
        }
        setStep('success');
    };

    const handleReset = () => {
        setStep('upload');
        setFile(null);
        setError(null);
        setNormalizedItems([]);
        setSelected({});
        setSavedCount(0);
        setFailedCount(0);
    };

    const toggleItem = (key: string) => {
        setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleAll = (value: boolean) => {
        setSelected(Object.fromEntries(normalizedItems.map((i) => [i.key, value])));
    };

    const selectedCount = Object.values(selected).filter(Boolean).length;

    // ============================================================================
    // RENDER
    // ============================================================================

    if (step === 'upload') {
        return (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Upload a confirmation PDF
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Drop a flight, hotel, car, or activity confirmation. We'll extract the details and let you review before adding to your trip.
                    </Typography>
                </Box>

                <Box
                    {...getRootProps()}
                    sx={{
                        border: '2px dashed',
                        borderColor: isDragActive ? '#2D6E6A' : file ? '#4CAF50' : '#ddd',
                        borderRadius: 3,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: isDragActive ? 'rgba(0,180,160,0.05)' : file ? 'rgba(76,175,80,0.05)' : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': { borderColor: '#2D6E6A', bgcolor: 'rgba(0,180,160,0.04)' },
                    }}
                >
                    <input {...getInputProps()} />
                    {file ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                            <PdfIcon sx={{ color: '#E53935', fontSize: 32 }} />
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography variant="body2" fontWeight={600}>{file.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {(file.size / 1024).toFixed(1)} KB
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); setFile(null); }}
                                sx={{ ml: 1 }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ) : (
                        <Box>
                            <UploadIcon sx={{ fontSize: 48, color: '#2D6E6A', mb: 1 }} />
                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                {isDragActive ? 'Drop the PDF here' : 'Drag & drop a PDF, or click to browse'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                One file at a time \u00b7 PDF only
                            </Typography>
                        </Box>
                    )}
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleUploadAndProcess}
                        disabled={!file}
                        startIcon={<UploadIcon />}
                        sx={{
                            bgcolor: '#2D6E6A',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            '&:hover': { bgcolor: '#009688' },
                        }}
                    >
                        Upload & extract
                    </Button>
                </Box>
            </Paper>
        );
    }

    if (step === 'processing') {
        return (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
                <CircularProgress sx={{ color: '#2D6E6A', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {isOcrPolling ? 'Running OCR on your PDF\u2026' : 'Reading your PDF\u2026'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {isOcrPolling
                        ? 'This is an image-based PDF. OCR may take 1-3 minutes.'
                        : 'Uploading and extracting trip details. This usually takes 10\u201320 seconds.'}
                </Typography>
            </Paper>
        );
    }

    if (step === 'review') {
        const grouped: Record<NormalizedItem['type'], NormalizedItem[]> = {
            flight: normalizedItems.filter((i) => i.type === 'flight'),
            hotel: normalizedItems.filter((i) => i.type === 'hotel'),
            car: normalizedItems.filter((i) => i.type === 'car'),
            activity: normalizedItems.filter((i) => i.type === 'activity'),
        };

        return (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Review extracted items
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {normalizedItems.length} found \u00b7 {selectedCount} selected
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" onClick={() => toggleAll(true)} sx={{ textTransform: 'none' }}>
                            Select all
                        </Button>
                        <Button size="small" onClick={() => toggleAll(false)} sx={{ textTransform: 'none' }}>
                            None
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ mb: 1 }} />

                {(['flight', 'hotel', 'car', 'activity'] as const).map((type) => {
                    const items = grouped[type];
                    if (items.length === 0) return null;
                    return (
                        <Box key={type} sx={{ mb: 2 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    letterSpacing: 0.5,
                                    display: 'block',
                                    mb: 0.5,
                                }}
                            >
                                {type === 'car' ? 'Transport' : type + 's'} ({items.length})
                            </Typography>
                            {items.map((item) => (
                                <Box
                                    key={item.key}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        py: 1,
                                        px: 1,
                                        borderRadius: 2,
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                                    }}
                                >
                                    <Checkbox
                                        checked={!!selected[item.key]}
                                        onChange={() => toggleItem(item.key)}
                                        sx={{ p: 0.5 }}
                                    />
                                    {iconForType(item.type)}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        >
                                            {item.title}
                                        </Typography>
                                        {item.subtitle && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {item.subtitle}
                                            </Typography>
                                        )}
                                    </Box>
                                    {item.payload.confirmation_number ? (
                                        <Chip
                                            size="small"
                                            label={String(item.payload.confirmation_number)}
                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                        />
                                    ) : null}
                                </Box>
                            ))}
                        </Box>
                    );
                })}

                {error && (
                    <Alert severity="error" sx={{ mt: 1, mb: 1, borderRadius: 2 }}>{error}</Alert>
                )}

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button onClick={handleReset} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveSelected}
                        disabled={selectedCount === 0}
                        startIcon={<CheckIcon />}
                        sx={{
                            bgcolor: '#2D6E6A',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            '&:hover': { bgcolor: '#009688' },
                        }}
                    >
                        Add {selectedCount} {selectedCount === 1 ? 'item' : 'items'}
                    </Button>
                </Box>
            </Paper>
        );
    }

    if (step === 'saving') {
        const total = Object.values(selected).filter(Boolean).length;
        const progress = total > 0 ? (savedCount + failedCount) / total : 0;
        return (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Adding items to your trip\u2026
                </Typography>
                <LinearProgress
                    variant="determinate"
                    value={progress * 100}
                    sx={{ my: 2, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="text.secondary">
                    {savedCount + failedCount} of {total} processed
                </Typography>
            </Paper>
        );
    }

    if (step === 'success') {
        return (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
                <CheckIcon sx={{ fontSize: 56, color: '#4CAF50', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Done
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Added {savedCount} {savedCount === 1 ? 'item' : 'items'} to your trip.
                    {failedCount > 0 && ` ${failedCount} ${failedCount === 1 ? 'item' : 'items'} could not be added.`}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<UploadIcon />}
                    sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#2D6E6A', color: '#2D6E6A' }}
                >
                    Upload another
                </Button>
            </Paper>
        );
    }

    // step === 'error'
    return (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
            <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
                {error || 'Something went wrong.'}
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    onClick={handleReset}
                    startIcon={<RetryIcon />}
                    sx={{
                        bgcolor: '#2D6E6A',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#009688' },
                    }}
                >
                    Try again
                </Button>
            </Box>
        </Paper>
    );
};

export default TripItemImportCard;
